import { AppDate } from '../envTypes';

export type OAuth2Scope =
  | 'user:basic_info'
  | 'community:followers'
  | 'community:subscribers'
  | 'leads:manage'
  | 'store:activities';

/**
 * OAuth2 grant types supported by the client
 */
export enum OAuth2GrantType {
  AuthorizationCode = 'authorization_code',
  ClientCredentials = 'client_credentials',
  RefreshToken = 'refresh_token',
  Implicit = 'implicit',
  Password = 'password',
}

/**
 * OAuth2 client status
 */
export enum OAuth2ClientStatus {
  Active = 'active',
  Inactive = 'inactive',
  Revoked = 'revoked',
}

/**
 * OAuth2 client interface
 * Represents an OAuth2 client application that can request authorization
 */
export interface OAuth2Client {
  /**
   * Client identifier (public)
   */
  clientId: string;

  /**
   * Client secret
   * Should be hashed/encrypted in storage
   */
  clientSecret: string;

  /**
   * Client name/display name
   */
  name: string;

  /**
   * Client description
   */
  description?: string;

  /**
   * Allowed redirect URIs for authorization code flow
   */
  redirectUris: string[];

  /**
   * Allowed grant types for this client
   */
  grantTypes: OAuth2GrantType[];

  /**
   * Default scopes that can be requested by this client
   */
  defaultScopes?: string[];

  /**
   * Allowed scopes (if empty, all scopes are allowed)
   */
  allowedScopes?: string[];

  /**
   * Client status
   */
  status: OAuth2ClientStatus;

  /**
   * User ID of the client owner/creator
   */
  ownerUserId?: string;

  /**
   * Application website URL
   */
  websiteUrl?: string;

  /**
   * Application logo URL
   */
  logoUrl?: string;

  /**
   * Access token expiration time in seconds
   * If not set, uses server default
   */
  accessTokenLifetime?: number;

  /**
   * Refresh token expiration time in seconds
   * If not set, uses server default
   */
  refreshTokenLifetime?: number;

  /**
   * Whether to require PKCE (Proof Key for Code Exchange) for authorization code flow
   */
  requirePkce?: boolean;

  /**
   * Whether to skip consent screen for this client
   */
  skipConsent?: boolean;

  /**
   * Timestamp when the client was created
   */
  createdAt: AppDate;

  /**
   * Timestamp when the client was last updated
   */
  updatedAt: AppDate;

  /**
   * Timestamp when the client was last used
   */
  lastUsedAt?: AppDate;
}

export enum OAuthorizationStatus {
  Active = 'active',
  Revoked = 'revoked',
  Expired = 'expired',
}

export enum RevokeReason {
  UserRevoked = 'user_revoked',
  AdminRevoked = 'admin_revoked',
}

export interface OAuth2Authorization {
  userId: string;

  /**
   * OAuth2 client identifiers
   */

  /**
   * OAuth2 client id
   */
  clientId: string;

  /**
   * Document ID of the OAuth2 client.
   */
  clientDocId: string;

  /**
   * Hash of the granted access token.
   */
  accessTokenHash: string;
  /**
   * Hash of the granted refresh token.
   */
  refreshTokenHash: string;

  /**
   * Absolute time when the refresh token expires.
   */
  refreshTokenExpireTime: AppDate;

  /**
   * Scopes granted in this authorization.
   */
  scopes?: string[];

  /**
   * Status of the authorization:
   * - 'active': Authorization is valid and usable.
   * - 'revoked': Authorization has been revoked and is no longer valid.
   * - 'expired': Authorization is expired due to token expiry.
   */
  status: OAuthorizationStatus;

  /**
   * Timestamps for important authorization events
   */
  createdAt: AppDate;

  updatedAt: AppDate;

  lastUsedAt?: AppDate;

  revokedAt?: AppDate;

  /**
   * Authorization context
   */
  context?: {
    /**
     * IP address captured at time of authorization (optional).
     */
    ipAddr?: string;

    /**
     * User agent string to identify the device/browser (optional).
     */
    userAgent?: string;
  };

  /**
   * Reason for revocation if applicable.
   */
  revokeReason?: RevokeReason;
}
